import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Platforms } from '@/common/constants';
import type { Option, Platform } from '@/common/types';
import { scrollElementIntoHorizontalView } from '@/common/utils';

import FilterChipButton from './FilterChipButton';
import HorizontalScrollView from '../HorizontalScrollView/HorizontalScrollView';

type FilterChipGroupPropsBase<ValueType extends string = string> = {
  /** Additional CSS classes to apply to the container. */
  className?: string;
  /** Array of filter options to display as chips */
  options: Option<ValueType>[];
  /** Currently selected filter value, or null if no filter is active */
  value: ValueType | null;
  /** Platform on which the filter chip group is being rendered. */
  platform?: Platform;
};

type TogglableFilterChipGroupProps<ValueType extends string = string> =
  FilterChipGroupPropsBase<ValueType> & {
    isTogglable?: true;
    /** Callback fired when a filter chip is clicked. Receives the new value or null if toggled off */
    onValueChange?: (newValue: ValueType | null) => unknown;
  };

type NonTogglableFilterChipGroupProps<ValueType extends string = string> =
  FilterChipGroupPropsBase<ValueType> & {
    isTogglable?: false;
    /** Callback fired when a filter chip is clicked. Receives the new value */
    onValueChange?: (newValue: ValueType) => unknown;
  };

export type FilterChipGroupProps<ValueType extends string = string> =
  | TogglableFilterChipGroupProps<ValueType>
  | NonTogglableFilterChipGroupProps<ValueType>;

const SCROLL_OFFSET = 32;

export default function FilterChipGroup<ValueType extends string>({
  className = '',
  options,
  value,
  platform = Platforms.MOBILE,
  isTogglable = true,
  onValueChange = () => null,
}: FilterChipGroupProps<ValueType>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    scrollElementIntoHorizontalView(containerRef.current, activeButtonRef.current, SCROLL_OFFSET);
  }, [value, options]);

  const handleButtonClick = (clickedFilterValue: ValueType) => {
    if (isTogglable) {
      (onValueChange as (newValue: ValueType | null) => unknown)(
        value === clickedFilterValue ? null : clickedFilterValue,
      );
      return;
    }

    onValueChange(clickedFilterValue);
  };

  return (
    // overflow-y-hidden is added just in case because sometimes the area is
    // scrollable vertically even though it shouldn't
    // see https://bitbucket.org/suzu_kindred/stats_widgets/pull-requests/209#comment-781791960
    <HorizontalScrollView
      ref={containerRef}
      className={twMerge(
        'flex items-center gap-[8px] font-robotoMedium font-medium text-[12px] leading-[16px]',
        className,
      )}
      platform={platform}
    >
      {options.map((option) => (
        <FilterChipButton
          key={option.value}
          ref={(ref) => {
            if (value === option.value) {
              activeButtonRef.current = ref;
            }
          }}
          isActive={value === option.value}
          onClick={() => handleButtonClick(option.value)}
        >
          {option.label}
        </FilterChipButton>
      ))}
    </HorizontalScrollView>
  );
}
